/**
 * Restoration · batch processing — serial chain runner for multiple files.
 *
 * Single-shot UX is fine for one record, but archivists routinely have
 * 30 cassettes / 80 vinyl rips from one source. Batch mode applies the
 * SAME chain + params (the recipe currently loaded in the UI) to every
 * uploaded file, serially, and offers per-file download as each
 * completes.
 *
 * Why serial, not parallel:
 *   • Single AudioContext per browser, no native worker support for our
 *     custom JS DSP modules (they touch AudioBuffer directly).
 *   • Memory: one 30 MB stereo WAV decodes to ~180 MB Float32 in heap;
 *     two in flight = OOM territory on iOS / older Android.
 *
 * Public API:
 *   const queue = RestorationBatch.create({
 *       files: [File, File, ...],
 *       chainBuilder: () => [{name:'derumble', ...}, ...],
 *       captureIntermediates: false,
 *       onItemStart: (idx, file) => {},
 *       onItemDone: (idx, {blob, name}, impact) => {},
 *       onItemError: (idx, err) => {},
 *       onProgress: (idx, stage, ratio) => {},
 *       signal: AbortSignal,
 *   });
 *   await queue.run();
 *
 * Output blobs are 16-bit PCM WAV (same encoder as the single-file path).
 */
(function () {
    'use strict';

    function audioBufferToWav16(buffer) {
        const numCh = buffer.numberOfChannels;
        const sr = buffer.sampleRate;
        const len = buffer.length;
        const bytesPerSample = 2;
        const blockAlign = numCh * bytesPerSample;
        const dataLen = len * blockAlign;
        const bufSize = 44 + dataLen;
        const ab = new ArrayBuffer(bufSize);
        const view = new DataView(ab);
        function writeStr(off, s) { for (let i = 0; i < s.length; i++) view.setUint8(off + i, s.charCodeAt(i)); }
        writeStr(0, 'RIFF');
        view.setUint32(4, 36 + dataLen, true);
        writeStr(8, 'WAVE');
        writeStr(12, 'fmt ');
        view.setUint32(16, 16, true);
        view.setUint16(20, 1, true);
        view.setUint16(22, numCh, true);
        view.setUint32(24, sr, true);
        view.setUint32(28, sr * blockAlign, true);
        view.setUint16(32, blockAlign, true);
        view.setUint16(34, 16, true);
        writeStr(36, 'data');
        view.setUint32(40, dataLen, true);
        let off = 44;
        const chans = [];
        for (let c = 0; c < numCh; c++) chans.push(buffer.getChannelData(c));
        for (let i = 0; i < len; i++) {
            for (let c = 0; c < numCh; c++) {
                let s = Math.max(-1, Math.min(1, chans[c][i]));
                view.setInt16(off, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
                off += 2;
            }
        }
        return new Blob([ab], { type: 'audio/wav' });
    }

    async function decodeFile(file) {
        const ac = new (window.AudioContext || window.webkitAudioContext)();
        const ab = await file.arrayBuffer();
        const buf = await ac.decodeAudioData(ab);
        try { ac.close(); } catch (_) {}
        return buf;
    }

    function noopHook() {}

    class BatchQueue {
        constructor(opts) {
            this.files = opts.files || [];
            this.chainBuilder = opts.chainBuilder || (() => []);
            this.captureIntermediates = !!opts.captureIntermediates;
            this.onItemStart = opts.onItemStart || noopHook;
            this.onItemDone  = opts.onItemDone  || noopHook;
            this.onItemError = opts.onItemError || noopHook;
            this.onProgress  = opts.onProgress  || noopHook;
            this.signal = opts.signal || null;
            this.results = [];
        }
        async run() {
            if (!window.AudioRestorationEngine) {
                throw new Error('AudioRestorationEngine not loaded');
            }
            for (let i = 0; i < this.files.length; i++) {
                if (this.signal && this.signal.aborted) {
                    throw Object.assign(new Error('aborted'), { name: 'AbortError' });
                }
                const f = this.files[i];
                this.onItemStart(i, f);
                try {
                    const buffer = await decodeFile(f);
                    const result = await window.AudioRestorationEngine.runChain(
                        buffer, this.chainBuilder(),
                        {
                            signal: this.signal,
                            captureIntermediates: this.captureIntermediates,
                            onProgress: (stage, ratio) => this.onProgress(i, stage, ratio),
                        }
                    );
                    const stem = (f.name || ('track_' + (i + 1))).replace(/\.[^.]+$/, '');
                    const blob = audioBufferToWav16(result.output);
                    const done = { idx: i, originalName: f.name, name: stem + '-restored.wav', blob: blob, impact: result.impact };
                    this.results.push(done);
                    this.onItemDone(i, done, result.impact);
                } catch (err) {
                    this.onItemError(i, err);
                }
                // Yield between items so the browser can paint progress UI
                await new Promise((r) => setTimeout(r, 30));
            }
            return this.results;
        }
    }

    window.RestorationBatch = {
        create: function (opts) { return new BatchQueue(opts); },
        _audioBufferToWav16: audioBufferToWav16, // exposed for shared use from blade
    };
})();
